/**
 * Shared inspector-panel form controls.
 *
 * Number/text inputs are uncontrolled and commit on the NATIVE `change` event
 * (fires on blur/Enter after an edit, never per keystroke), so typing is
 * never interrupted by re-renders. React's `onChange` is the `input` event
 * (per keystroke), so a real `change` listener is attached instead. After
 * every commit the input is re-keyed so its displayed value snaps back to the
 * canonical (possibly clamped) model value; external changes (e.g. drags)
 * re-key it via the `value` prop.
 *
 * A room-editor field that commits to a panel key (the applyPanelInput
 * protocol) is a `CommitLengthField`, `CommitLengthInput` or
 * `CommitNumberField`, naming its key once. A choice from a fixed list is a
 * `ChoiceField`. In a read-only panel (`usePanelReadOnly`) every field below
 * shows its value as a `ReadOnlyField`: label and plain text, never an editor.
 */

import { css } from '@emotion/react';
import { commitPanelFeet, commitPanelNumber, useFloorPlanEditorState, useLengthText } from '@kaiizen/kai-floor-plan-editor-core';
import { useEffect, useRef, useState } from 'react';
import { ink } from '../../styles';
import { LengthInput, type LengthInputProps } from '../LengthInput';
import { FIELD_ROW_STYLE } from './chrome';
import { usePanelReadOnly } from './panelReadOnly';

/** A value shown as it is: ink, at the size of the row's label. */
const READ_ONLY_VALUE_STYLE = css`
  label: READ_ONLY_VALUE_STYLE;
  color: ${ink}; font-size: 13px;
`;

export interface CommitInputProps {
  /** Canonical formatted value from the model. */
  value: string;
  /** Called with the raw input string when the value is committed. */
  onCommit: (raw: string) => void;
}

function useNativeChangeCommit(value: string, onCommit: (raw: string) => void) {
  const [epoch, setEpoch] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // Re-attach whenever the key parts change (the input DOM node is remounted).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => {
      if (el.value !== value) commitRef.current(el.value);
      setEpoch(n => n + 1); // re-key → display refreshes to the canonical value
    };
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, [value, epoch]);

  return { ref, inputKey: `${value}\u0000${epoch}` };
}

const blurOnEnter = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  if (e.key === 'Enter') e.currentTarget.blur();
};

export interface NumInputProps extends CommitInputProps {
  /** data-set attribute: the applyPanelInput key, and the E2E selector. */
  setKey: string;
  step?: number;
  min?: number;
  placeholder?: string;
}

/** Bare number input: the control of a `NumField`. */
function NumInput({ setKey, value, onCommit, step = 0.5, min, placeholder }: NumInputProps): React.JSX.Element {
  const { ref, inputKey } = useNativeChangeCommit(value, onCommit);
  return (
    <input
      key={inputKey}
      ref={ref}
      type='number'
      step={step}
      min={min}
      placeholder={placeholder}
      data-set={setKey}
      defaultValue={value}
      onKeyDown={blurOnEnter}
    />
  );
}

/** Labeled row with a number input committing on native change. */
export function NumField({ label, ...rest }: { label: string } & NumInputProps): React.JSX.Element {
  const readOnly = usePanelReadOnly();
  if (readOnly) return <ReadOnlyField id={rest.setKey} label={label} value={rest.value} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label>{label}</label>
      <NumInput {...rest} />
    </div>
  );
}

/** Labeled row hosting the shared length editor (all panel measurements, in the preferred units). */
export function LengthField({ label, ...rest }: { label: string } & LengthInputProps): React.JSX.Element {
  const lengthText = useLengthText();
  if (usePanelReadOnly()) return <ReadOnlyField id={rest.setKey ?? label} label={label} value={lengthText(rest.value)} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label>{label}</label>
      <LengthInput {...rest} />
    </div>
  );
}

export interface CommitLengthInputProps {
  /** The panel key the length commits to; also the `data-set` E2E anchor. */
  setKey: string;
  /** Decimal feet from the model; null renders blank. */
  value: number | null;
  /** Permit a leading minus (free-element X/Y overhanging the room). */
  allowNegative?: boolean;
}

/** The shared length editor, committing to the room editor's panel key `setKey`. */
export function CommitLengthInput({ setKey, value, allowNegative }: CommitLengthInputProps): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  const lengthText = useLengthText();
  if (usePanelReadOnly()) return <output id={setKey} css={READ_ONLY_VALUE_STYLE}>{lengthText(value)}</output>;
  return (
    <LengthInput
      setKey={setKey}
      value={value}
      allowNegative={allowNegative}
      onCommit={valueFeet => commitPanelFeet(floorPlanEditorState, setKey, valueFeet)}
    />
  );
}

/** Labeled row hosting a `CommitLengthInput`. */
export function CommitLengthField({ label, ...rest }: { label: string } & CommitLengthInputProps): React.JSX.Element {
  const lengthText = useLengthText();
  if (usePanelReadOnly()) return <ReadOnlyField id={rest.setKey} label={label} value={lengthText(rest.value)} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label>{label}</label>
      <CommitLengthInput {...rest} />
    </div>
  );
}

/** A `NumField` committing to the room editor's panel key `setKey`. */
export function CommitNumberField({ setKey, ...rest }: { label: string } & Omit<NumInputProps, 'onCommit'>): React.JSX.Element {
  const floorPlanEditorState = useFloorPlanEditorState();
  return <NumField setKey={setKey} {...rest} onCommit={raw => commitPanelNumber(floorPlanEditorState, setKey, raw)} />;
}

export interface TextFieldProps extends CommitInputProps {
  id: string;
  label: string;
  maxLength?: number;
  placeholder?: string;
}

/** Labeled row with a text input committing on native change. */
export function TextField({ id, label, value, onCommit, maxLength, placeholder }: TextFieldProps): React.JSX.Element {
  const { ref, inputKey } = useNativeChangeCommit(value, onCommit);
  if (usePanelReadOnly()) return <ReadOnlyField id={id} label={label} value={value} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label>{label}</label>
      <input
        key={inputKey}
        ref={ref}
        type='text'
        id={id}
        maxLength={maxLength}
        placeholder={placeholder}
        defaultValue={value}
        onKeyDown={blurOnEnter}
      />
    </div>
  );
}

/** Labeled row showing a value as plain text: every value of a read-only panel, a measurement shown for reference, or a value no list of choices may replace (see `ChoiceField`). */
export function ReadOnlyField({ id, label, value }: { id: string; label: string; value: string }): React.JSX.Element {
  return (
    <div css={FIELD_ROW_STYLE}>
      <label htmlFor={id}>{label}</label>
      <output id={id} css={READ_ONLY_VALUE_STYLE}>{value}</output>
    </div>
  );
}

export interface ChoiceFieldProps<Value extends string> {
  id: string;
  label: string;
  /** The stored value, which may be one this version doesn't know. */
  value: string;
  /** Every known choice as [value, label], in picker order. */
  options: readonly [Value, string][];
  onChange: (value: Value) => void;
}

/**
 * Labeled row picking one of a fixed set of choices. A value outside the set
 * came from a newer version: it shows as it is, read-only, so a save can
 * never swap it for a known choice.
 */
export function ChoiceField<Value extends string>({ id, label, value, options, onChange }: ChoiceFieldProps<Value>): React.JSX.Element {
  const readOnly = usePanelReadOnly();
  const known = options.find(([choice]) => choice === value);
  if (!known || readOnly) return <ReadOnlyField id={id} label={label} value={known?.[1] ?? value} />;
  return (
    <div css={FIELD_ROW_STYLE}>
      <label htmlFor={id}>{label}</label>
      <select id={id} value={value} onChange={changeEvent => onChange(changeEvent.currentTarget.value as Value)}>
        {options.map(([choice, choiceLabel]) => <option key={choice} value={choice}>{choiceLabel}</option>)}
      </select>
    </div>
  );
}
